import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Send, Sparkles, AlertCircle, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

export default function ChatReply() {
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [reply, setReply] = useState("");
    const [sending, setSending] = useState(false);
    const [loading, setLoading] = useState(true);
    const scrollRef = useRef(null);

    const fetchData = async () => {
        try {
            const r = await api.get(`/chat/reply/${token}`);
            setData(r.data);
            setErr("");
        } catch (e) {
            setErr(e?.response?.data?.detail || "Could not load conversation");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); const t = setInterval(fetchData, 4000); return () => clearInterval(t); }, [token]);
    useEffect(() => { scrollRef.current?.scrollTo({ top: 9e9 }); }, [data]);

    const send = async () => {
        if (!reply.trim() || sending) return;
        setSending(true);
        try {
            await api.post(`/chat/reply/${token}`, { text: reply.trim() });
            setReply("");
            fetchData();
        } catch {}
        finally { setSending(false); }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-cream flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-gold-dark animate-spin" />
            </div>
        );
    }

    if (err) {
        return (
            <div className="min-h-screen bg-cream flex items-center justify-center px-6">
                <div className="max-w-md text-center bg-white border border-cream-border p-10">
                    <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
                    <h1 className="font-playfair text-2xl text-navy mb-2">Link no longer valid</h1>
                    <p className="font-outfit text-sm text-muted-foreground mb-6">{err}</p>
                    <Link to="/admin" className="inline-block bg-navy text-gold px-6 py-3 text-xs uppercase tracking-wider font-semibold">Open Admin Dashboard</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-cream-light flex flex-col" data-testid="chat-reply-page">
            {/* Header */}
            <header className="bg-navy text-cream px-6 py-4 flex items-center justify-between border-b border-gold/40">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full border border-gold flex items-center justify-center">
                        <Sparkles className="w-5 h-5 text-gold" />
                    </div>
                    <div className="flex flex-col leading-none">
                        <span className="font-script text-2xl text-gold -mb-1">Signature</span>
                        <span className="font-playfair text-[10px] tracking-[0.3em] uppercase">Live Reply</span>
                    </div>
                </div>
                <div className="text-right">
                    <div className="font-outfit text-sm font-semibold">{data.visitor_name}</div>
                    <div className="font-outfit text-xs text-cream/70">{data.visitor_contact}</div>
                </div>
            </header>

            {/* Conversation */}
            <main ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-6 max-w-3xl w-full mx-auto space-y-3">
                {data.messages.map((m, i) => (
                    <div key={i}>
                        {m.user && (
                            <div className="flex justify-start mb-1">
                                <div className="max-w-[75%] px-4 py-2.5 text-sm bg-white border border-cream-border text-navy font-outfit">
                                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground block mb-1">{data.visitor_name}</span>
                                    {m.user}
                                </div>
                            </div>
                        )}
                        {m.bot && (
                            <div className="flex justify-start mb-1">
                                <div className="max-w-[75%] px-4 py-2.5 text-sm bg-cream border border-cream-border text-navy font-outfit">
                                    <span className="text-[10px] uppercase tracking-wider text-gold-dark block mb-1">Signa (AI)</span>
                                    {m.bot}
                                </div>
                            </div>
                        )}
                        {m.agent && (
                            <div className="flex justify-end mb-1">
                                <div className="max-w-[75%] px-4 py-2.5 text-sm bg-navy text-cream font-outfit">
                                    <span className="text-[10px] uppercase tracking-wider text-gold block mb-1">You</span>
                                    {m.agent}
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </main>

            {/* Reply input */}
            <footer className="border-t border-cream-border bg-white px-6 py-4">
                <div className="max-w-3xl mx-auto flex gap-2">
                    <input
                        data-testid="reply-input"
                        value={reply}
                        onChange={(e) => setReply(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && send()}
                        placeholder={`Type your reply to ${data.visitor_name}…`}
                        className="flex-1 border border-cream-border px-4 py-3 text-sm font-outfit focus:outline-none focus:border-gold"
                    />
                    <button
                        data-testid="reply-send"
                        onClick={send}
                        disabled={sending || !reply.trim()}
                        className="bg-gold text-navy px-6 font-outfit font-semibold uppercase tracking-wider text-xs hover:bg-gold-light disabled:opacity-40 flex items-center gap-2"
                    >
                        <Send className="w-4 h-4" /> Send
                    </button>
                </div>
                <p className="text-[10px] text-muted-foreground text-center mt-2 font-outfit italic">
                    The visitor sees your reply live in their chat widget. No login required — link is private and unique to this conversation.
                </p>
            </footer>
        </div>
    );
}
